import React, { useCallback, useEffect } from "react";
import { CookieNames } from "../cookie/Cookie";
import { useCookies } from "react-cookie";

declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
    }
}

export const addGtmContainer = (
    containerId: string,
    dataLayerName: string = "dataLayer",
    consentSettings: {
        ad_user_data: string;
        ad_personalization: string;
        ad_storage: string;
        analytics_storage: string;
        personalization_storage: string;
        functionality_storage: string;
        security_storage: string;
    }
) => {
    if (!containerId) return;

    if (dataLayerName !== "dataLayer") {
        const gtagScript = document.createElement("script");
        gtagScript.async = true;

        gtagScript.textContent = `
      window.${dataLayerName} = window.${dataLayerName} || [];
      window['gtag_${dataLayerName}'] = function(){ ${dataLayerName}.push(arguments); }
      window.customGtags = window.customGtags || {};
      window.customGtags['${dataLayerName}'] = window['gtag_${dataLayerName}'];
      window['gtag_${dataLayerName}']('consent', 'default', ${JSON.stringify(consentSettings)});
    `;
        document.head.appendChild(gtagScript);
    } else {

        if (typeof window.gtag !== "function") {
            const globalScript = document.createElement("script");
            globalScript.async = true;
            globalScript.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){ dataLayer.push(arguments); }
        gtag('consent', 'default', ${JSON.stringify(consentSettings)});
      `;
            document.head.appendChild(globalScript);
        } else {
            window.gtag("consent", "default", consentSettings);
        }
    }

    if (document.querySelector(`script[src*="gtm.js?id=${containerId}"]`)) {
        console.warn(`GTM container ${containerId} is already added.`);
        return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src =
        `https://www.googletagmanager.com/gtm.js?id=${containerId}` +
        (dataLayerName !== "dataLayer" ? `&l=${dataLayerName}` : "");
    document.head.appendChild(script);

    (window as any)[dataLayerName] = (window as any)[dataLayerName] || [];
    (window as any)[dataLayerName].push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
    });
};

const GoogleTagManagerHeader: React.FC = () => {
    const [cookies] = useCookies([
        CookieNames.Performance,
        CookieNames.Functional,
        CookieNames.Marketing,
        CookieNames.IsCookieSaved,
    ]);

    const adUserData = cookies[CookieNames.Marketing] ? "granted" : "denied";
    const adPersonalization = cookies[CookieNames.Marketing] ? "granted" : "denied";
    const adStorage = cookies[CookieNames.Marketing] ? "granted" : "denied";
    const analyticsStorage = cookies[CookieNames.Performance] ? "granted" : "denied";
    const personalizationStorage = cookies[CookieNames.Functional]
        ? "granted"
        : "denied";

    const updateConsent = useCallback(() => {
        const consentUpdate = {
            ad_user_data: adUserData,
            ad_personalization: adPersonalization,
            ad_storage: adStorage,
            analytics_storage: analyticsStorage,
            personalization_storage: personalizationStorage,
            functionality_storage: "granted",
            security_storage: "granted",
        };

        if (typeof window.gtag === "function") {
            window.gtag("consent", "update", consentUpdate);
        }

        if ((window as any).customGtags) {
            Object.keys((window as any).customGtags).forEach((key) => {
                const customGtag = (window as any).customGtags[key];
                if (typeof customGtag === "function") {
                    customGtag("consent", "update", consentUpdate);
                }
            });
        }
    }, [adUserData, adPersonalization, adStorage, analyticsStorage, personalizationStorage]);

    useEffect(() => {
        updateConsent();
    }, [updateConsent]);

    return null;
};

GoogleTagManagerHeader.displayName = "GoogleTagManagerHeader";
export default GoogleTagManagerHeader;
